import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import Text from '../components/shared/Text';

interface PurchaseItem {
    id: string;
    product_name: string;
    sku?: string;
    quantity: number;
    unit_price: number;
    total: number;
    tax_amount: number;
    tax_rate: number;
}

interface Payment {
    id: string;
    payment_number: string;
    amount: number;
    payment_method: string;
    payment_date: string;
}

interface Purchase {
    id: string;
    purchase_number: string;
    status: string;
    vendor_name: string;
    purchase_date: string;
    total_amount: number;
    paid_amount: number;
    balance_due: number;
    landed_cost: number;
    subtotal?: number;
    tax_amount?: number;
    total_tax?: number;
    total_cgst?: number;
    total_sgst?: number;
    total_igst?: number;
    discount_amount?: number;
    items: PurchaseItem[];
    payments: Payment[];
}

function PurchaseShow() {
    const { id } = useParams();
    const [purchase, setPurchase] = useState<Purchase | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadPurchase();
    }, [id]);

    const loadPurchase = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/purchases/${id}`);
            if (response.success) {
                setPurchase(response.data);
            } else {
                setError(response.message || 'Failed to load purchase');
            }
        } catch (err: any) {
            setError(err.message || 'An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="mt-16 p-8 text-center">
                <Text variant="body" color="slate" weight="medium">Loading purchase...</Text>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mt-16 p-8 text-center">
                <Text variant="body" weight="medium" className="text-red-500">{error}</Text>
            </div>
        );
    }

    if (!purchase) {
        return (
            <div className="mt-16 p-8 text-center">
                <Text variant="body" color="slate" weight="medium">Purchase not found</Text>
            </div>
        );
    }

    return (
        <div className="mt-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <div className="flex items-center gap-3">
                        <Link to="/purchases" className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </Link>
                        <Text variant="h1" color="primary">Purchase {purchase.purchase_number}</Text>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${purchase.balance_due <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                            {purchase.status?.toUpperCase()}
                        </span>
                    </div>
                    <div className="ml-11">
                        <Text variant="body" color="slate" className="mt-1">{purchase.vendor_name}</Text>
                    </div>
                </div>
                <div className="flex items-center gap-3 ml-11 sm:ml-0">
                    <Link to={`/purchases/${purchase.id}/edit`} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition-colors shadow-sm text-sm">
                        Edit Purchase
                    </Link>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                    {/* Purchase Details */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                        <div className="flex items-center justify-between mb-4">
                            <Text variant="h3" color="primary">Purchase Information</Text>
                            <div className="text-right">
                                <Text variant="small" color="slate">Tax Summary</Text>
                                <Text variant="body" weight="bold" className="text-indigo-600">₹{(purchase.tax_amount || 0).toLocaleString()}</Text>
                            </div>
                        </div>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
                            <div>
                                <dt><Text variant="small" color="slate">Purchase Date</Text></dt>
                                <dd><Text variant="body" weight="medium" color="primary">{new Date(purchase.purchase_date).toLocaleDateString(undefined, { dateStyle: 'long' })}</Text></dd>
                            </div>
                            <div>
                                <dt><Text variant="small" color="slate">Vendor</Text></dt>
                                <dd><Text variant="body" weight="medium" color="primary">{purchase.vendor_name}</Text></dd>
                            </div>
                            <div>
                                <dt><Text variant="small" color="slate">Total Tax (GST)</Text></dt>
                                <dd><Text variant="body" weight="bold" color="primary" className="font-mono">₹{(purchase.tax_amount || 0).toLocaleString()}</Text></dd>
                            </div>
                            <div>
                                <dt><Text variant="small" color="slate">Paid Amount</Text></dt>
                                <dd><Text variant="body" weight="bold" className="text-emerald-600 font-mono">₹{purchase.paid_amount?.toLocaleString()}</Text></dd>
                            </div>
                            <div>
                                <dt><Text variant="small" color="slate">Balance Due</Text></dt>
                                <dd><Text variant="body" weight="bold" className="text-rose-600 font-mono">₹{purchase.balance_due?.toLocaleString()}</Text></dd>
                            </div>
                            <div>
                                <dt><Text variant="small" color="slate">Total Cost (with Tax)</Text></dt>
                                <dd><Text variant="body" weight="bold" className="text-indigo-600 font-mono">₹{purchase.total_amount?.toLocaleString()}</Text></dd>
                            </div>
                        </dl>
                    </div>

                    {/* Items Table */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-200">
                            <Text variant="h3" color="primary">Items</Text>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr>
                                        <th className="px-6 py-3 text-left"><Text variant="label" color="slate">Product</Text></th>
                                        <th className="px-6 py-3 text-center"><Text variant="label" color="slate">Qty</Text></th>
                                        <th className="px-6 py-3 text-right"><Text variant="label" color="slate">Cost</Text></th>
                                        <th className="px-6 py-3 text-right"><Text variant="label" color="slate">Tax</Text></th>
                                        <th className="px-6 py-3 text-right"><Text variant="label" color="slate">Total</Text></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {purchase.items?.map(item => (
                                        <tr key={item.id}>
                                            <td className="px-6 py-4">
                                                <Text variant="body" weight="medium" color="primary">{item.product_name}</Text>
                                                <Text variant="small" color="slate">SKU: {item.sku || '-'} | {item.tax_rate}% Tax</Text>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <Text variant="body" color="primary">{item.quantity}</Text>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <Text variant="mono" color="primary" className="text-sm">₹{item.unit_price?.toLocaleString()}</Text>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <Text variant="mono" color="slate" className="text-xs">₹{item.tax_amount?.toLocaleString()}</Text>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <Text variant="mono" weight="bold" color="primary">₹{item.total?.toLocaleString()}</Text>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Payment Ledger */}
                <div className="space-y-6">
                    <div className="bg-slate-900 rounded-2xl shadow-xl p-8 text-white">
                        <div className="text-[10px] font-black opacity-40 tracking-[0.2em] mb-6 uppercase">Purchase Valuation</div>
                        <div className="space-y-3 mb-6">
                            <div className="flex justify-between items-center">
                                <span className="text-sm opacity-60 font-medium">Subtotal</span>
                                <span className="text-sm font-mono opacity-60">₹{(purchase.subtotal || 0).toLocaleString()}</span>
                            </div>
                            {(purchase.total_igst || 0) > 0 ? (
                                <div className="flex justify-between items-center">
                                    <span className="text-sm opacity-60">IGST</span>
                                    <span className="text-sm font-mono opacity-60">₹{purchase.total_igst?.toLocaleString()}</span>
                                </div>
                            ) : (
                                <>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm opacity-60">CGST</span>
                                        <span className="text-sm font-mono opacity-60">₹{(purchase.total_cgst || (purchase.tax_amount || 0) / 2).toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm opacity-60">SGST</span>
                                        <span className="text-sm font-mono opacity-60">₹{(purchase.total_sgst || (purchase.tax_amount || 0) / 2).toLocaleString()}</span>
                                    </div>
                                </>
                            )}
                            {(purchase.discount_amount || 0) > 0 && (
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-rose-400">Discount</span>
                                    <span className="text-sm font-mono text-rose-400">-₹{purchase.discount_amount?.toLocaleString()}</span>
                                </div>
                            )}
                        </div>
                        <div className="pt-6 border-t border-white/10 text-center">
                            <div className="text-[10px] opacity-30 tracking-[0.25em] mb-1 uppercase">Total Cost</div>
                            <div className="text-4xl font-black font-mono">₹{purchase.total_amount?.toLocaleString()}</div>
                        </div>
                    </div>

                    {/* Payment Ledger Card */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="text-base font-black text-slate-900 uppercase tracking-widest">Payment Ledger</h3>
                            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                                (purchase.balance_due || 0) <= 0
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : (purchase.paid_amount || 0) > 0
                                        ? 'bg-amber-100 text-amber-700'
                                        : 'bg-rose-100 text-rose-700'
                            }`}>
                                {(purchase.balance_due || 0) <= 0 ? 'Paid' : (purchase.paid_amount || 0) > 0 ? 'Partial' : 'Unpaid'}
                            </span>
                        </div>

                        {/* Summary */}
                        <div className="space-y-3 mb-5 p-4 bg-slate-50 rounded-xl">
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Total Bill</span>
                                <span className="text-sm font-black text-slate-800 font-mono">₹{(purchase.total_amount || 0).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-bold text-indigo-500 uppercase tracking-widest">Incl. Tax</span>
                                <span className="text-sm font-black text-indigo-500 font-mono">₹{(purchase.tax_amount || 0).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-bold text-emerald-600 uppercase tracking-widest">Paid Amount</span>
                                <span className="text-sm font-black text-emerald-600 font-mono">₹{(purchase.paid_amount || 0).toLocaleString()}</span>
                            </div>
                            <div className="h-px bg-slate-200" />
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-black text-rose-600 uppercase tracking-widest">Balance Due</span>
                                <span className="text-base font-black text-rose-600 font-mono">₹{(purchase.balance_due || 0).toLocaleString()}</span>
                            </div>
                        </div>

                        {/* Transaction List */}
                        {purchase.payments?.length > 0 ? (
                            <div className="space-y-3">
                                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Transactions</div>
                                {purchase.payments.map((p) => (
                                    <div key={p.id} className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                                        <div>
                                            <div className="text-xs font-black text-slate-700">{p.payment_number}</div>
                                            <div className="text-[10px] text-slate-400 font-medium capitalize">
                                                {p.payment_method?.replace('_', ' ')} · {new Date(p.payment_date).toLocaleDateString('en-IN')}
                                            </div>
                                        </div>
                                        <span className="text-sm font-black text-emerald-700 font-mono">₹{p.amount.toLocaleString()}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-xs text-slate-400 text-center py-4">No payments recorded yet.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PurchaseShow;
